<template>
  <div class="h-full relative z-0 flex overflow-hidden mb-5">
    <div v-if="is_loading" class="absolute bottom-1 right-2">
      <Spinner />
    </div>

    <Form
      ref="form"
      v-slot="{ values }"
      :initial-values="initial_values"
      :validation-schema="schema"
      class="flex-1 flex flex-col h-full relative z-0 overflow-y-auto focus:outline-none xl:order-last pt-8 pl-8 pr-8 pb-1.5"
      @submit="createTeam">
      <main class="flex items-start flex-col w-full">
        <div class="justify-self-start text-[20px] text-2xl pb-2 text-light-grey-text dark:text-dark-body-text">
          {{ $t("Create New Team") }}
        </div>
        <div class="w-full flex flex-col gap-2">
          <TextInput
            show-label
            classes="sm:w-1/3 w-full"
            :label="$t('Team Name')"
            autocomplete="off"
            input-classes="dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-md"
            type="text"
            :is-box-background="false"
            name="name" />
          <TextArea
            classes="sm:w-1/3 w-full mt-3"
            :rows="8"
            input-classes="dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-md"
            show-label
            :label="$t('Description')"
            name="description" />
        </div>

        <div class="flex sm:flex-row sm:justify-between sm:items-end items-start flex-col mb-4 gap-2 w-full mt-10">
          <div class="sm:justify-self-start text-md font-medium text-light-grey-text dark:text-dark-body-text">
            {{ $t("Team Members") }}
            <p class="mt-2 text-sm text-gray-500">
              {{ $t("Select team") }} {{ values.name }} {{ $t("members and set their roles") }}
            </p>
          </div>
          <div class="flex flex-row justify-between items-center sm:ml-auto gap-2">
            <input
              id="search"
              v-model="searched_user"
              autocomplete="off"
              type="text"
              name="search"
              class="w-full text-md dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light"
              :placeholder="$t('Find team members')">

            <SelectInput
              :disabled="teams.length === 0"
              name="type"
              :items="teams"
              label-attr="name"
              :allow-null="true"
              :classes="'w-full'"
              :value-attr="null"
              :label="$t('Teams')"
              :show-label="false"
              :placeholder="$t('Teams')"
              :is-box-background="true"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  selected_team = $event
                }
              " />
          </div>
        </div>
      </main>

      <!-- Testing User Table -->
      <div class="max-h-[500px] min-w-full border border-light-divider-border dark:border-dark-divider-border rounded-md">
        <table class="min-w-full relative">
          <thead
            class="w-full bg-light-box-background dark:bg-dark-box-background transition ease-in-out duration-side-menu-transition-duration">
            <draggable
              v-model="headers"
              filter=".ignore-elements"
              :component-data="{
                type: 'transition-group',
                name: !drag ? 'flip-list' : null
              }"
              v-bind="dragOptions"
              item-key="name"
              class="border-light-divider-border dark:border-dark-divider-border max-w-full text-left text-common-grey transition ease-in-out duration-side-menu-transition-duration"
              tag="tr"
              @start="drag = true"
              @end="drag = false">
              <template #item="{ element, index }">
                <th v-if="element.type == 'checkbox'" class="!rounded-tl-md ignore-elements px-6 py-3 tracking-wider">
                  <input
                    v-model="select_all_users"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                </th>
                <th
                  v-else-if="element.type == 'header' || element.type == 'radio' || element.type == 'avatar'"
                  :class="{ '!rounded-tr-md': index == headers.length - 1 || index == 0 }"
                  class="px-6 py-3 font-normal whitespace-nowrap">
                  {{ element.name }}
                </th>
                <th v-else-if="element.type == 'nonHeader'" class="rounded-tr-md px-6 py-3 tracking-wider whitespace-nowrap" />
              </template>
            </draggable>
          </thead>
          <tbody
            class="dark:border-dark-divider-border bg-light-screen-background divide-y divide-light-divider-border dark:divide-dark-divider-border dark:bg-dark-screen-background transition ease-in-out duration-side-menu-transition-duration rounded-lg">
            <tr
              v-for="(user, index) in users"
              :key="user.id"
              class="group shadow-sm text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue hover:cursor-pointer hover:text-dark-body-text transition ease-in-out duration-side-menu-transition-duration text-sm rounded-lg"
              :class="[index + 1 === users?.length ? '' : '']">
              <td class="px-6 py-3 tracking-wider w-5">
                <input
                  v-model="user.checked"
                  type="checkbox"
                  class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
                  @change="toggleUserFromSelectedList(user, user.checked)">
              </td>
              <td
                v-for="header in headers.filter((el, i) => i > 0)"
                :key="header.id"
                class="px-6 py-3 font-medium tracking-wider whitespace-nowrap">
                <span v-if="header.type === 'header' && header.id !== 'team_manager' && header.id !== 'role_name'">
                  {{ user[header.id] }}
                </span>
                <input
                  v-else-if="header.id === 'team_manager' && selected_users.has(user.id)"
                  v-model="user.is_manager"
                  type="checkbox"
                  class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                <div v-else-if="header.id === 'role_name'" class="">
                  <Listbox v-model="user.role" as="div" class="flex-shrink-0">
                    <ListboxLabel class="sr-only">
                      {{ $t("Role") }}
                    </ListboxLabel>
                    <div class="relative">
                      <ListboxButton
                        class="relative inline-flex items-center rounded-full py-2 px-2 bg-gray-50 dark:bg-dark-box-background text-sm font-medium text-gray-500 whitespace-nowrap hover:bg-gray-100 sm:px-3">
                        <Tag
                          :class="[
                            user.role === null ? 'text-gray-300' : 'text-gray-500 dark:text-gray-200',
                            'flex-shrink-0 h-5 w-5 sm:-ml-1'
                          ]"
                          aria-hidden="true" />
                        <span :class="[user.role === null ? '' : 'text-gray-900 dark:text-gray-200', 'hidden truncate sm:ml-2 sm:block']">{{
                          user.role === null
                            ? $t("Label")
                            : $t($store.state.auth.user.organization.roles.find((x) => x.id == user.role.id)?.name)
                        }}</span>
                      </ListboxButton>

                      <transition
                        leave-active-class="transition ease-in duration-100"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
                        <ListboxOptions
                          class="absolute right-0 z-10 mt-1 w-52 bg-white dark:bg-dark-box-background shadow max-h-56 rounded-lg py-3 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm text-gray-700 hover:cursor-pointer hover:text-gray-700">
                          <ListboxOption
                            v-for="role in $store.state.auth.user.organization.roles"
                            :key="role.name"
                            v-slot="{ active }"
                            :value="role"
                            as="template">
                            <li
                              :class="[
                                active ? 'bg-gray-100 dark:bg-common-grey' : 'bg-white dark:bg-dark-box-background',
                                'cursor-default select-none relative py-2 px-3'
                              ]">
                              <div class="flex items-center">
                                <span class="block font-medium truncate dark:text-white">
                                  {{ $t(role.name) }}
                                </span>
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                </div>

                <!-- todo: added logic for removing users -->
                <span v-else-if="updating" @click="() => console.log('Hello')">{{ $t("Remove") }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Testing User Table End -->
      <button ref="submit" style="display: none">{{ $t("Submit") }}</button>
    </Form>
    <Modal v-model:is-open="show_error_dialog">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col text-center justify-center items-center h-full px-4">
          <div class="flex justify-center">
            <CircleAlert class="m-4 h-20 w-20 text-red-400" aria-hidden="true" />
          </div>
          <p class="mt-2 text-xl text-gray-500">
            {{ error_message }}
          </p>
          <button
            type="button"
            class="mt-10 group inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="is_required_email">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col text-center justify-center items-center h-full px-4">
          <div class="flex justify-center">
            <CircleAlert class="m-4 h-20 w-20 text-red-400" aria-hidden="true" />
          </div>
          <p class="mt-2 text-xl text-gray-500">
            {{ error_message }}
          </p>
          <div class="flex gap-2 pt-2 w-full flex-row justify-between mt-6">
            <button type="button" class="cancel-btn w-1/2 m-0" @click="closeModal">{{ $t("Cancel") }}</button>
            <button class="primary-btn w-1/2" @click="emailFilterUsers()">
              <span>{{ $t("Continue") }}</span>
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import * as Yup from "yup"
import dataService from "@/services/data.service"
import Modal from "@/components/shared/Modal.vue"
import { ListboxLabel, Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue"
import { Tag, CircleAlert } from "lucide-vue-next"
import authService from "@/services/auth.service"
import { Form } from "vee-validate"
import draggable from "vuedraggable"
import Spinner from "@/components/shared/LoadingSpinner.vue"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  components: {
    SelectInput,
    Modal,
    TextInput,
    TextArea,
    Form,
    draggable,
    Spinner,
    ListboxLabel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Tag,
    CircleAlert
  },
  props: {
    updating: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const schema = Yup.object().shape({
      name: Yup.string().required(),
      description: Yup.string()
    })

    const initial_values = { name: "", description: "" }

    return {
      schema,
      initial_values
    }
  },
  data() {
    return {
      sf_users: [],
      sf_users_filter: "",
      filtered_sf_users: [],
      // form data
      headers: [
        { type: "checkbox", id: "Checkbox", name: "_checkbox" },
        { type: "avatar", id: "avatar", name: this.$t("Avatar") },
        { type: "header", id: "first_name", name: this.$t("First Name") },
        { type: "header", id: "last_name", name: this.$t("Last Name") },
        { type: "header", id: "email", name: this.$t("Email") },
        { type: "header", id: "role_name", name: this.$t("Role") },
        { type: "header", id: "team", name: this.$t("Team") },
        { type: "header", id: "team_manager", name: this.$t("Team Manager") },
        { type: "nonHeader", id: "remove", name: this.$t("Remove") }
      ],
      drag: false,
      error_message: null,
      show_error_dialog: false,
      is_required_email: false,
      users: [],
      team_managers: [],
      selected_users: new Map(),
      select_all_users: false,
      teams: [],
      selected_team: null,
      searched_user: "",
      is_loading: false
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "user header",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  watch: {
    sf_users_filter() {
      if (this.sf_users_filter != null && this.sf_users_filter != "") {
        this.filtered_sf_users = this.sf_users.filter((u) => {
          return u["email"].toLowerCase().search(this.sf_users_filter) != -1 || u["name"].toLowerCase().search(this.sf_users_filter) != -1
        })
      } else {
        this.filtered_sf_users = this.sf_users
      }
    },
    select_all_users() {
      this.users = this.users.map((user) => {
        user["checked"] = this.select_all_users
        this.toggleUserFromSelectedList(user, this.select_all_users)
        return user
      })
    },
    selected_team() {
      if (this.selected_team) {
        const userlist = this.selected_team.users.filter((i) => !this.users.includes(i.id))
        userlist.map((user) => {
          user.role = this.$store.state.auth.user.organization.roles.find((x) => x.id == user.role)
        })
        this.users = userlist
      }
      if (!this.selected_team) {
        this.fetchUsers()
      }
    },
    searched_user() {
      if (!this.searched_user) {
        this.fetchUsers()
      }
      const users = this.users.filter((u) => {
        let searchedItem = this.searched_user.toLowerCase()
        let firstName = u.first_name.toLowerCase()
        let lastName = u.last_name.toLowerCase()
        if (firstName.includes(searchedItem) || lastName.includes(searchedItem)) {
          return u
        }
      })
      this.users = users
    }
  },
  activated() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["New Team"])
    this.register_listeners()
  },
  mounted() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["New Team"])
  },
  async created() {
    // this.$store.dispatch("ui/showLoading")
    //this.register_listeners()
    this.fetchUsers()
    this.selected_users.clear()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.SUBMIT_NEW_TEAM, () => {
        this.$refs.submit.click()
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.SUBMIT_NEW_TEAM)
    },
    async fetchUsers() {
      const teamList = await dataService.getOrganizationTeams()
      this.teams = teamList.data.results
      const fetchedUsers = new Promise((resolve) => {
        authService.fetchUsers().then((res) => resolve(res.data.results))
      })
      this.users = await fetchedUsers
      this.users = this.users.filter((u) => utils.isEmail(u.email))
      if (this.users.length > 1) {
        this.users = this.users.map((u) => {
          let teams = []
          this.teams.map((team) => {
            team.users.map((user) => {
              if (user.id == u.id) {
                teams.push(team.name)
              }
              team.team_managers.map((id) => {
                if (u.id == id) {
                  u["team_manager"] = true
                }
              })
            })
          })
          u["team"] = String(teams)
          u["checked"] = this.selected_users.has(u.id)
          u["is_manager"] = false
          u["status"] = u.is_active ? u.is_active : false
          this.$store.dispatch("ui/hideLoading")
          return u
        })
      }
    },
    toggleUserFromSelectedList(user, selected) {
      if (selected) {
        this.selected_users.set(user.id, user)
      } else {
        this.selected_users.delete(user.id)
      }
    },
    emailFilterUsers() {
      Array.from(this.selected_users.values()).map((user) => {
        if (!user.email) {
          this.selected_users.delete(user.id)
        }
      })
      this.$refs.submit.click()
    },
    createTeam(values) {
      //Check is any required field
      this.show_error_dialog = false
      this.is_required_email = false
      let arrSelectedUsers = Array.from(this.selected_users.values())
      if (arrSelectedUsers.length === 0) {
        this.show_error_dialog = true
        this.error_message = this.$t("Should select members")
      } else {
        let isExistManager = false
        let isExistEmptyEmail = false
        arrSelectedUsers.map((user) => {
          if (user.is_manager) {
            isExistManager = true
          }
          if (!user.email) {
            isExistEmptyEmail = true
          }
        })
        if (!isExistManager) {
          this.show_error_dialog = true
          this.error_message = this.$t("Should select team manager")
          return
        } else if (isExistEmptyEmail) {
          this.is_required_email = true
          this.error_message = this.$t(
            "You can not save a team with a Team Member that does not have an email address. When you click on CONTINUE, members who do not have an email address will be excluded from the team"
          )
          return
        }
      }
      //If all required field not emty
      if (!this.show_error_dialog && !this.is_required_email) {
        this.show_error_dialog = false
        this.team_managers = []
        let users = []
        let members = []
        Array.from(this.selected_users.values()).map((user) => {
          if (user.is_manager) {
            this.team_managers.push(user.id)
          }
          members.push(user.id)
          if (user.role) {
            users.push({
              user: user.id,
              role: user.role.id
            })
          }
        })
        let team = {
          name: values.name,
          description: values.description,
          organization: this.$store.state.organization.id,
          users_access: users,
          users: members,
          team_managers: this.team_managers
        }
        dataService
          .addOrganizationTeam(team)
          .then(() => {
            this.$toast.open({
              message: this.$t("Team") + ` "${values.name}" ` + this.$t("created successfully"),
              position: "top",
              type: "success",
              duration: 3000
            })
            this.$router.push("/admin/teams")
          })
          .catch(() => {
            this.$toast.open({
              message: this.$t("Error creating a Team, please try again!"),
              position: "top",
              type: "error",
              duration: 5000
            })
          })
      }
    }
  }
}
</script>
